import { useCallback, useRef, useState, useMemo, useEffect } from "react";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import { OTRO_NO_LISTADO, etiquetaTipoDocumento } from "../../config/captacionDocumentosTipos";
import VisorImagenDocumento, { BarraAccionesImagen } from "../captaciones/VisorImagenDocumento";
import {
  META_OTROS_FICHA_PROP,
  TIPOS_RAPIDOS_FICHA_PROP,
  esTipoRapidoFichaProp,
  getTiposRestoFichaPropOrdenados,
} from "../../config/propiedadDocumentacionTipos";
import ModalGenerarEncargoFicha from "./ModalGenerarEncargoFicha";
import { abrirPestanaEncargoGenerado } from "../../utils/encargoFichaModelos";
import { mostrarToastVitalCasa } from "../../utils/mostrarToastVitalCasa";

const ACCEPT_DOCS =
  "application/pdf,image/jpeg,image/png,image/webp,image/gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.odt";

function IconoLupa({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function mimeFromFile(file) {
  return (file && file.type) || "";
}

function defaultNombreDoc(file) {
  const n = file?.name || "Documento";
  return n.replace(/\.[^.]+$/, "") || n;
}

function formatFecha(isoOrMs) {
  if (isoOrMs == null || isoOrMs === "") return "—";
  const d = typeof isoOrMs === "number" ? new Date(isoOrMs) : new Date(isoOrMs);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function previewKind(mime, fileName) {
  const m = (mime || "").toLowerCase();
  const low = (fileName || "").toLowerCase();
  if (m.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/.test(low)) return "image";
  if (m === "application/pdf" || low.endsWith(".pdf")) return "pdf";
  return "other";
}

export default function SeccionDocumentacion(props) {
  const { documentos, setDocumentos, setDocumentosEliminar, propiedadId, defaultsExclusiva } = props;
  const { tv } = usePropiedadUi(props);
  const { puede } = usePermiso();
  const puedeAltaDocAdjuntar = puede("doc_adjuntar", "alta");
  const puedeModificarDocRenombrarTipo = puede("doc_renombrar_tipo", "modificar");
  const puedeVerDocVerDescargar = puede("doc_ver_descargar", "ver");
  const puedeBajaDocEliminar = puede("doc_eliminar", "baja");

  const inputRef = useRef(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [seleccionId, setSeleccionId] = useState(null);
  const [editandoNombreId, setEditandoNombreId] = useState(null);
  const [modalAmpliar, setModalAmpliar] = useState(false);
  const [imgRot, setImgRot] = useState(0);
  const [imgZoom, setImgZoom] = useState(1);
  const [modalEncargo, setModalEncargo] = useState(false);

  const tiposRestoOrdenados = useMemo(() => getTiposRestoFichaPropOrdenados(), []);

  const seleccionado = documentos.find((d) => d.id === seleccionId) || null;

  useEffect(() => {
    if (documentos.length === 0) {
      setSeleccionId(null);
      return;
    }
    if (!seleccionId || !documentos.some((d) => d.id === seleccionId)) {
      setSeleccionId(documentos[0].id);
    }
  }, [documentos, seleccionId]);

  useEffect(() => {
    setModalAmpliar(false);
  }, [seleccionId]);

  useEffect(() => {
    if (!modalAmpliar) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModalAmpliar(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [modalAmpliar]);

  const etiquetaSeleccionado =
    seleccionado?.tipo_codigo === OTRO_NO_LISTADO && (seleccionado?.descripcion_tipo || "").trim()
      ? `${etiquetaTipoDocumento(OTRO_NO_LISTADO)}: ${String(seleccionado.descripcion_tipo).trim()}`
      : etiquetaTipoDocumento(seleccionado?.tipo_codigo || "") || seleccionado?.tipo_codigo || "";

  const procesarArchivos = (fileList) => {
    if (!puedeAltaDocAdjuntar) return;
    const nuevas = [];
    Array.from(fileList || []).forEach((file) => {
      const mime = mimeFromFile(file);
      if (!mime && !file.name) return;
      const id = crypto.randomUUID();
      const preview = URL.createObjectURL(file);
      nuevas.push({
        id,
        file,
        existente: false,
        preview,
        url: preview,
        nombre_documento: defaultNombreDoc(file),
        tipo_codigo: "documento_generico",
        descripcion_tipo: "",
        mime,
        fecha: file.lastModified || Date.now(),
      });
    });
    if (nuevas.length === 0) return;
    setDocumentos((prev) => [...prev, ...nuevas]);
    setSeleccionId(nuevas[nuevas.length - 1].id);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setArrastrando(false);
    procesarArchivos(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setState estable
  }, []);

  const onDragOverZone = useCallback((e) => {
    e.preventDefault();
    setArrastrando(true);
  }, []);

  const eliminarDoc = (doc) => {
    if (doc.existente && doc.id) {
      setDocumentosEliminar((prev) => (prev.includes(doc.id) ? prev : [...prev, doc.id]));
    }
    if (doc.preview && doc.file) {
      try {
        URL.revokeObjectURL(doc.preview);
      } catch {
        /* ignore */
      }
    }
    setDocumentos((prev) => prev.filter((x) => x.id !== doc.id));
    setSeleccionId((sid) => (sid === doc.id ? null : sid));
    setEditandoNombreId((eid) => (eid === doc.id ? null : eid));
  };

  const pk = seleccionado
    ? previewKind(
      seleccionado.mime,
      seleccionado.file?.name || seleccionado.nombre_original || seleccionado.nombre_documento,
    )
    : null;
  const srcPreview = seleccionado?.preview || seleccionado?.url || "";

  useEffect(() => {
    setImgRot(0);
    setImgZoom(1);
  }, [srcPreview]);

  const aplicarTipoDoc = (docId, tipoCodigo) => {
    setDocumentos((prev) =>
      prev.map((x) =>
        x.id === docId
          ? {
              ...x,
              tipo_codigo: tipoCodigo,
              descripcion_tipo: tipoCodigo === OTRO_NO_LISTADO ? x.descripcion_tipo : "",
            }
          : x
      )
    );
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        {tv("PDF, imágenes, Word, Excel u hojas de cálculo. Arrastra archivos o usa «Adjuntar». Vista previa a la derecha.")}
      </p>

      <div className="flex flex-col lg:flex-row gap-4 min-h-[420px]">
        {/* Lista + drop — mitad izquierda en escritorio */}
        <div className="w-full lg:w-1/2 flex flex-col gap-3 min-w-0">
          <div
            onDrop={onDrop}
            onDragOver={onDragOverZone}
            onDragLeave={() => setArrastrando(false)}
            className={
              "border-2 border-dashed rounded-xl p-4 transition-all " +
              (arrastrando ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-gray-400")
            }
          >
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">{tv("Documentos")} ({documentos.length})</span>
                <button
                  type="button"
                  onClick={() => {
                    if (!propiedadId) {
                      mostrarToastVitalCasa("Guarda la propiedad antes de generar el encargo", { tipo: "error" });
                      return;
                    }
                    setModalEncargo(true);
                  }}
                  className="text-sm px-3 py-1.5 rounded-lg border border-blue-600 text-blue-700 bg-white font-medium hover:bg-blue-50"
                >
                  {tv("Generar nuevo encargo")}
                </button>
              </div>
              {puedeAltaDocAdjuntar && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
              >
                + {tv("Adjuntar")}
              </button>
              )}
            </div>
            {puedeAltaDocAdjuntar && (
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT_DOCS}
              className="hidden"
              onChange={(e) => {
                procesarArchivos(e.target.files);
                e.target.value = "";
              }}
            />
            )}
          </div>

          <div
            onDrop={onDrop}
            onDragOver={onDragOverZone}
            onDragLeave={() => setArrastrando(false)}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white flex-1 min-h-[200px]"
          >
            {documentos.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">{tv("No hay documentos. Arrastra aquí encima o usa Adjuntar.")}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-2 w-[38%]">{tv("Nombre")}</th>
                    <th className="px-2 py-2 w-[42%]">{tv("Tipo")}</th>
                    <th className="px-2 py-2 w-[14%] whitespace-nowrap">{tv("Fecha")}</th>
                    <th className="px-1 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {documentos.map((doc) => {
                    const activa = seleccionId === doc.id;
                    const codigo = doc.tipo_codigo || "documento_generico";
                    const enRapido = esTipoRapidoFichaProp(codigo);
                    const valorPrincipal = enRapido ? codigo : META_OTROS_FICHA_PROP;
                    return (
                      <tr
                        key={doc.id}
                        onClick={() => setSeleccionId(doc.id)}
                        className={
                          "border-b border-gray-100 cursor-pointer " +
                          (activa ? "bg-blue-50/80" : "hover:bg-gray-50")
                        }
                      >
                        <td className="px-2 py-2 align-middle min-w-0">
                          {editandoNombreId === doc.id ? (
                            <input
                              autoFocus
                              className="w-full px-1.5 py-1 border border-blue-300 rounded text-sm"
                              value={doc.nombre_documento || ""}
                              onChange={(e) =>
                                setDocumentos((prev) =>
                                  prev.map((x) => (x.id === doc.id ? { ...x, nombre_documento: e.target.value } : x))
                                )
                              }
                              onBlur={() => setEditandoNombreId(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "Escape") setEditandoNombreId(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="truncate font-medium text-gray-800" title={doc.nombre_documento}>
                                {doc.nombre_documento || "—"}
                              </span>
                              {puedeModificarDocRenombrarTipo && (
                              <button
                                type="button"
                                title="Editar nombre"
                                className="shrink-0 p-0.5 text-blue-600 hover:text-blue-800 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditandoNombreId(doc.id);
                                  setSeleccionId(doc.id);
                                }}
                              >
                                <span className="text-base leading-none" aria-hidden>
                                  ✎
                                </span>
                              </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 align-middle min-w-0" onClick={(e) => e.stopPropagation()}>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
                            {tv("Principales")}
                          </div>
                          <select
                            className="w-full max-w-full px-1 py-1 border border-gray-200 rounded text-xs bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                            value={valorPrincipal}
                            aria-label="Tipo de documento — Principales"
                            disabled={!puedeModificarDocRenombrarTipo}
                            title={!puedeModificarDocRenombrarTipo ? "No tienes permiso para modificar" : undefined}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === META_OTROS_FICHA_PROP) {
                                const actual = doc.tipo_codigo || "documento_generico";
                                const enResto = tiposRestoOrdenados.some((t) => t.codigo === actual);
                                const siguiente = enResto
                                  ? actual
                                  : tiposRestoOrdenados[0]?.codigo || "documento_generico";
                                aplicarTipoDoc(doc.id, siguiente);
                              } else {
                                aplicarTipoDoc(doc.id, v);
                              }
                            }}
                          >
                            {TIPOS_RAPIDOS_FICHA_PROP.map((t) => (
                              <option key={t.codigo} value={t.codigo}>
                                {t.etiqueta}
                              </option>
                            ))}
                            <option value={META_OTROS_FICHA_PROP}>{tv("Otros documentos…")}</option>
                          </select>
                          {!enRapido && (
                            <>
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mt-1 mb-0.5">
                                {tv("Otros documentos")}
                              </div>
                              <select
                                className="w-full max-w-full px-1 py-1 border border-gray-200 rounded text-xs bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                                value={codigo}
                                aria-label="Tipo de documento — Otros documentos"
                                disabled={!puedeModificarDocRenombrarTipo}
                                title={!puedeModificarDocRenombrarTipo ? "No tienes permiso para modificar" : undefined}
                                onChange={(e) => aplicarTipoDoc(doc.id, e.target.value)}
                              >
                                {tiposRestoOrdenados.map((t) => (
                                  <option key={t.codigo} value={t.codigo}>
                                    {t.etiqueta}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                          {doc.tipo_codigo === OTRO_NO_LISTADO && (
                            <input
                              type="text"
                              className="w-full mt-1 px-1 py-0.5 border border-gray-200 rounded text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder={tv("Describe el documento…")}
                              value={doc.descripcion_tipo || ""}
                              disabled={!puedeModificarDocRenombrarTipo}
                              title={!puedeModificarDocRenombrarTipo ? "No tienes permiso para modificar" : undefined}
                              onChange={(e) =>
                                setDocumentos((prev) =>
                                  prev.map((x) => (x.id === doc.id ? { ...x, descripcion_tipo: e.target.value } : x))
                                )
                              }
                            />
                          )}
                        </td>
                        <td className="px-2 py-2 align-middle text-xs text-gray-600 whitespace-nowrap">
                          {formatFecha(doc.fecha)}
                        </td>
                        <td className="px-1 py-2 align-middle text-center">
                          {puedeBajaDocEliminar && (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 text-lg leading-none"
                            title="Quitar"
                            onClick={(e) => {
                              e.stopPropagation();
                              eliminarDoc(doc);
                            }}
                          >
                            ×
                          </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Visor — mitad derecha */}
        {puedeVerDocVerDescargar && (
        <div className="w-full lg:w-1/2 flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-gray-50 min-h-[280px]">
          <div className="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 px-3 py-2 border-b border-gray-200 bg-white">
            <span className="shrink-0">{tv("Vista previa")}</span>
            <div className="flex items-center gap-2 min-w-0 justify-end flex-wrap">
              {seleccionado && srcPreview && pk === "image" && (
                <BarraAccionesImagen
                  src={srcPreview}
                  title={seleccionado.nombre_documento || "Documento"}
                  rot={imgRot}
                  setRot={setImgRot}
                  zoom={imgZoom}
                  setZoom={setImgZoom}
                />
              )}
              {seleccionado && srcPreview && (pk === "pdf" || pk === "image") && (
              <button
                type="button"
                onClick={() => setModalAmpliar(true)}
                className="flex items-center gap-1.5 normal-case font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg border border-blue-200 hover:bg-blue-50"
                title="Ampliar para leer mejor"
              >
                <IconoLupa className="shrink-0" />
                <span className="hidden sm:inline">{tv("Ampliar")}</span>
              </button>
              )}
            </div>
          </div>
          <div className="flex-1 min-h-[240px] flex items-center justify-center p-2">
            {!seleccionado && <p className="text-sm text-gray-400">{tv("Selecciona un documento")}</p>}
            {seleccionado && pk === "image" && srcPreview && (
              <div className="w-full h-[min(70vh,520px)] min-h-[240px]">
                <VisorImagenDocumento
                  src={srcPreview}
                  title={seleccionado.nombre_documento || "Documento"}
                  hideToolbar
                  rot={imgRot}
                  setRot={setImgRot}
                  zoom={imgZoom}
                  setZoom={setImgZoom}
                />
              </div>
            )}
            {seleccionado && pk === "pdf" && srcPreview && (
              <iframe title="Vista previa PDF" src={srcPreview} className="w-full h-[min(70vh,520px)] rounded border border-gray-200 bg-white" />
            )}
            {seleccionado && pk === "other" && (
              <div className="text-center px-4">
                <p className="text-sm text-gray-600 mb-3">{tv("No hay vista previa integrada para este formato.")}</p>
                {srcPreview && (
                  <a
                    href={srcPreview}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    {tv("Abrir / descargar")}
                  </a>
                )}
                <p className="text-xs text-gray-400 mt-2 max-w-xs mx-auto">{etiquetaSeleccionado}</p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {puedeVerDocVerDescargar && modalAmpliar && seleccionado && srcPreview && (pk === "pdf" || pk === "image") && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa ampliada"
          onClick={() => setModalAmpliar(false)}
        >
          <div
            className="relative w-full max-w-[min(96vw,1200px)] h-[min(92vh,900px)] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
              <span className="text-sm font-medium text-gray-800 truncate pr-2" title={seleccionado.nombre_documento}>
                {seleccionado.nombre_documento || tv("Documento")}
              </span>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {pk === "image" && (
                  <BarraAccionesImagen
                    src={srcPreview}
                    title={seleccionado.nombre_documento || "Documento"}
                    rot={imgRot}
                    setRot={setImgRot}
                    zoom={imgZoom}
                    setZoom={setImgZoom}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setModalAmpliar(false)}
                  className="shrink-0 px-3 py-1.5 text-sm font-semibold rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800"
                >
                  {tv("Cerrar")}
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-neutral-100 flex items-center justify-center p-2">
              {pk === "image" && (
                <div className="w-full h-full min-h-[70vh]">
                  <VisorImagenDocumento
                    src={srcPreview}
                    title={seleccionado.nombre_documento || "Documento"}
                    hideToolbar
                    rot={imgRot}
                    setRot={setImgRot}
                    zoom={imgZoom}
                    setZoom={setImgZoom}
                  />
                </div>
              )}
              {pk === "pdf" && (
                <iframe title="PDF ampliado" src={srcPreview} className="w-full h-full min-h-[70vh] rounded border border-gray-300 bg-white" />
              )}
            </div>
          </div>
        </div>
      )}
      <ModalGenerarEncargoFicha
        abierto={modalEncargo}
        onCerrar={() => setModalEncargo(false)}
        propiedadId={propiedadId}
        defaultsExclusiva={defaultsExclusiva}
        onConfirmar={(sel) => {
          setModalEncargo(false);
          if (!propiedadId) return;
          abrirPestanaEncargoGenerado(propiedadId, sel);
        }}
      />
    </div>
  );
}
